import produce from 'immer';
import { ChatbotConversationUserActionType, AssistantEphemeralContextSubType, ChatbotConversationUserActionSubType } from '../types.js';
import { isChatbotUserMessage } from './guardsChatbotMessage.js';

const prompts = {
  [ChatbotConversationUserActionType.SUMMARIZE]: {
    [ChatbotConversationUserActionSubType.TEXT]: `Provide a succint 1-2 sentence summary of the following text.

No preambles, no bullet points, just give the summary.`,
    [ChatbotConversationUserActionSubType.SELECTION]: `Provide a succint 1-2 sentence summary for the active document(s).

No preambles, no bullet points, just give the summary.`
  },
  [ChatbotConversationUserActionType.EXPLAIN_LIKE_IM_FIVE]: {
    [ChatbotConversationUserActionSubType.TEXT]: `Provide an explanation of the following text. Assume i don't know anything about the topic. Explain it like i'm 16 years old.

No preambles, no bullet points, just give the explanation.`,
    [ChatbotConversationUserActionSubType.SELECTION]: `Provide an explanation for the active document(s). Assume i don't know anything about the topic. Explain it like i'm 16 years old.

No preambles, no bullet points, just give the explanation.`
  },
  [ChatbotConversationUserActionType.LIST_POINTS]: {
    [ChatbotConversationUserActionSubType.TEXT]: `Provide a highly condensed summary of the following text:

1. For texts longer than 50 words, add up to 3 brief bullet points that:
   - Highlight key ideas or themes
   - Are extremely concise (aim for 8-12 words each)
   - Connect to broader context where relevant

For texts shorter than 50 words, add 0-2 bullet points only if necessary to capture essential additional information.

Use markdown. Prioritize brevity and relevance. The entire output should not exceed 70 words, even for lengthy texts. Start directly with the content, omitting any labels or headers:`,
    [ChatbotConversationUserActionSubType.SELECTION]: `Provide a condensed summary for the active document(s):

1. Assess the relatedness of the documents, looking for even abstract thematic connections.

2. If documents are related, share a similar thematic focus (even abstractly), or there is only one document:
   a. Follow with 3-5 bullet points that synthesize key themes across all documents.
      - Each bullet should be concise (10-15 words) and highlight main ideas or contrasts.
   b. End the summary here. Do not add any additional sections or bullet points.

3. Only as a last resort, if there are absolutely no possible thematic connections between the documents:
   a. For each document or if possible group of related documents (up to a maximum of 5):
      - Provide a brief subheading (group theme or document title) as a 3rd level heading.
      - Write 1-2 sentences (max 30 words) summarizing the main idea or purpose.
      - List 1-2 bullet points summarizing key points (10-15 words each).
   b. After summarizing all documents, stop. Do not add any concluding remarks or additional themes.

4. Use markdown formatting.

5. Prioritize brevity and relevance. The entire output must not exceed 200 words, even for multiple or lengthy documents.

6. Start directly with the content and end immediately after the last bullet point or document summary. Do not include any introductory or concluding sections.`
  },
  [ChatbotConversationUserActionType.TODO_LIST]: {
    [ChatbotConversationUserActionSubType.SELECTION]: `Generate a to-do list based on the content active document(s).

No preambles, just give me the to-do list as markdown bullet points.`
  },
  [ChatbotConversationUserActionType.TWEET]: {
    [ChatbotConversationUserActionSubType.SELECTION]: `Write a catchy tweet based on the content active document(s). If there's more than one, try to find overarching themes.

No preambles, just give me the tweet.`
  },
  [ChatbotConversationUserActionType.EMAIL]: {
    [ChatbotConversationUserActionSubType.SELECTION]: `Write a short email body discussing the content of the active document(s). Say
what you found interesting or what the key takeaways are for you. Avoid
wordiness and too many adjectives.

Keep it short at 3-5 sentences. Make sure to use paragraphs of 1-2 sentences as
necessary, to make reading easier.

Use the following format:
Subject: [Subject of the email]
Body: [Body of the email here]

No preambles, just give me the email.`
  },
  [ChatbotConversationUserActionType.CLEANUP]: {
    [ChatbotConversationUserActionSubType.SELECTION]: `Clean up the content of active document(s). Remove any unnecessary information,
fix grammar and formatting using markdown. Do not put the whole text in a code block.

No preambles, just give me the cleaned version.`
  }
};
const populateActionPrompt = (messages, contexts) => {
  const fileContext = contexts.find((context) => context.subType === AssistantEphemeralContextSubType.File);
  return [
    produce(messages, (draft) => {
      const lastMessage = draft[messages.length - 1];
      if (!isChatbotUserMessage(lastMessage) || !lastMessage.action) return;
      const prompt = prompts[lastMessage.action.type][lastMessage.action.subType];
      if (!prompt) return;
      if (lastMessage.action.subType === ChatbotConversationUserActionSubType.TEXT) {
        lastMessage.content = `${prompt}
${lastMessage.content}`;
      } else {
        lastMessage.content = prompt;
      }
    }),
    fileContext
  ];
};

export { populateActionPrompt };
//# sourceMappingURL=populateActionPrompt.js.map
